/**
 * Master–detail anchor health: site summary cards (left) + searchable anchor table (right).
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { SatelliteHealthIcon } from '../../branding/spatialNavIcons';
import {
  CheckCircle as OnlineIcon,
  Error as OfflineIcon,
  HelpOutline as UnassignedIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { AppTextField } from '../design-system';
import { TableDetailSplitLayout } from '../ui/TableDetailSplitLayout';
import { EnhancedTable, type FilterConfig, type TableColumn } from '../ui/EnhancedTable';
import { scrollbarSx } from '../../theme/scrollbar';
import { useTheme } from '@mui/material/styles';
import type { OpsDeviceHealth, SiteAnchorHealthRow } from '@cnapse/shared/schemas.js';

dayjs.extend(relativeTime);

const SITE_LIST_MIN_WIDTH = 260;
const SITE_LIST_MAX_WIDTH = 420;
const SITE_LIST_DEFAULT_WIDTH = 320;

const HEALTH_COLORS: Record<OpsDeviceHealth, 'success' | 'warning' | 'error' | 'default' | 'info'> = {
  healthy: 'success',
  degraded: 'warning',
  unhealthy: 'error',
  unknown: 'default',
  not_applicable: 'info',
};

interface SiteAnchorHealthSiteSummary {
  tenantId: string;
  siteId: string;
  siteName: string | null;
  totalAnchors: number;
  assignedCount: number;
  onlineCount: number;
  offlineCount: number;
  unassignedPointsCount: number;
  unassignedHardwareCount: number;
  needsAttentionCount: number;
  siteLocationGatewaySerial: string | null;
  gatewayConfigSyncHealth: string | null;
}

function siteKey(tenantId: string, siteId: string): string {
  return `${tenantId}::${siteId}`;
}

function rowSiteKey(row: SiteAnchorHealthRow): string {
  return siteKey(row.tenantId, row.siteId);
}

function aggregateAnchorSiteSummaries(rows: SiteAnchorHealthRow[]): SiteAnchorHealthSiteSummary[] {
  const map = new Map<string, SiteAnchorHealthSiteSummary>();

  for (const row of rows) {
    const key = rowSiteKey(row);
    let summary = map.get(key);
    if (!summary) {
      summary = {
        tenantId: row.tenantId,
        siteId: row.siteId,
        siteName: row.siteName ?? null,
        totalAnchors: 0,
        assignedCount: 0,
        onlineCount: 0,
        offlineCount: 0,
        unassignedPointsCount: 0,
        unassignedHardwareCount: 0,
        needsAttentionCount: 0,
        siteLocationGatewaySerial: row.siteLocationGatewaySerial ?? null,
        gatewayConfigSyncHealth: row.gatewayConfigSyncHealth ?? null,
      };
      map.set(key, summary);
    }

    summary.totalAnchors += 1;
    if (row.assigned) summary.assignedCount += 1;
    if (row.assignmentState === 'unassigned_point') summary.unassignedPointsCount += 1;
    if (row.assignmentState === 'unassigned_hardware') summary.unassignedHardwareCount += 1;
    if (row.online === true) summary.onlineCount += 1;
    if (row.online === false) summary.offlineCount += 1;
    if (
      row.overallHealth === 'unhealthy'
      || row.overallHealth === 'degraded'
      || row.assignmentState !== 'assigned'
    ) {
      summary.needsAttentionCount += 1;
    }
  }

  return Array.from(map.values()).sort((a, b) => {
    const aLabel = a.siteName ?? a.siteId;
    const bLabel = b.siteName ?? b.siteId;
    return aLabel.localeCompare(bLabel);
  });
}

function EllipsisCell({ text, title }: { text: string; title?: string }) {
  return (
    <Tooltip title={title ?? text}>
      <Box
        component="span"
        sx={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
        }}
      >
        {text}
      </Box>
    </Tooltip>
  );
}

interface SiteAnchorHealthCardProps {
  site: SiteAnchorHealthSiteSummary;
  selected: boolean;
  tenantLabel?: string | null;
  onSelect: () => void;
}

function SiteAnchorHealthCard({ site, selected, tenantLabel, onSelect }: SiteAnchorHealthCardProps) {
  const siteLabel = site.siteName ?? site.siteId;
  const showSiteId = siteLabel !== site.siteId;
  const needsAttention = site.needsAttentionCount > 0;

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: selected ? 'primary.main' : 'divider',
        borderWidth: selected ? 2 : 1,
        bgcolor: selected ? 'action.selected' : 'background.paper',
      }}
    >
      <CardActionArea onClick={onSelect}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" alignItems="flex-start" gap={1} flexWrap="wrap" useFlexGap>
            <Box minWidth={0} sx={{ flex: '1 1 100px' }}>
              <Typography variant="subtitle2" fontWeight={600} noWrap>
                {siteLabel}
              </Typography>
              {showSiteId && (
                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                  {site.siteId}
                </Typography>
              )}
              {tenantLabel && (
                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                  {tenantLabel}
                </Typography>
              )}
            </Box>
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ flex: '1 1 auto', justifyContent: 'flex-end' }}
            >
              {needsAttention && !selected && (
                <Chip label="!" size="small" color="warning" sx={{ minWidth: 28 }} />
              )}
              <Chip
                size="small"
                variant="outlined"
                icon={<SatelliteHealthIcon />}
                label={`${site.assignedCount} assigned`}
              />
              <Chip
                size="small"
                variant="outlined"
                icon={<OnlineIcon />}
                label={`${site.onlineCount} online`}
                color="success"
              />
              {site.offlineCount > 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  icon={<OfflineIcon />}
                  label={`${site.offlineCount} offline`}
                  color="error"
                />
              )}
            </Stack>
          </Stack>

          {(site.unassignedPointsCount > 0 || site.unassignedHardwareCount > 0) && (
            <Stack direction="row" spacing={0.75} mt={1} flexWrap="wrap" useFlexGap>
              {site.unassignedPointsCount > 0 && (
                <Chip
                  size="small"
                  icon={<UnassignedIcon />}
                  label={`${site.unassignedPointsCount} open points`}
                  color="warning"
                  variant="outlined"
                />
              )}
              {site.unassignedHardwareCount > 0 && (
                <Chip
                  size="small"
                  icon={<UnassignedIcon />}
                  label={`${site.unassignedHardwareCount} unassigned HW`}
                  color="warning"
                  variant="outlined"
                />
              )}
            </Stack>
          )}

          {site.siteLocationGatewaySerial && (
            <Typography variant="caption" color="text.secondary" display="block" mt={1} noWrap>
              Gateway {site.siteLocationGatewaySerial}
              {site.gatewayConfigSyncHealth ? ` · ${site.gatewayConfigSyncHealth}` : ''}
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export interface AnchorHealthSplitViewProps {
  rows: SiteAnchorHealthRow[];
  rowsLoading?: boolean;
  tenantNameById?: (tenantId: string) => string | null;
  showTenantOnCards?: boolean;
  initialSiteKey?: string | null;
  /** Provider ops: show manufacturer, model, and firmware columns */
  showDeviceMetadata?: boolean;
}

export default function AnchorHealthSplitView({
  rows,
  rowsLoading = false,
  tenantNameById,
  showTenantOnCards = false,
  initialSiteKey = null,
  showDeviceMetadata = false,
}: AnchorHealthSplitViewProps) {
  const theme = useTheme();
  const [siteSearch, setSiteSearch] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(initialSiteKey);

  const [anchorPage, setAnchorPage] = useState(1);
  const [anchorPageSize, setAnchorPageSize] = useState(25);
  const [anchorSearch, setAnchorSearch] = useState('');
  const [anchorFilters, setAnchorFilters] = useState<Record<string, string>>({
    assignmentState: '',
    overallHealth: '',
  });

  const siteSummaries = useMemo(() => aggregateAnchorSiteSummaries(rows), [rows]);

  const filteredSites = useMemo(() => {
    const q = siteSearch.trim().toLowerCase();
    if (!q) return siteSummaries;
    return siteSummaries.filter((site) => {
      const tenantName = tenantNameById?.(site.tenantId) ?? '';
      const hay = `${site.siteName ?? ''} ${site.siteId} ${tenantName}`.toLowerCase();
      return hay.includes(q);
    });
  }, [siteSummaries, siteSearch, tenantNameById]);

  const selectedSite = useMemo(
    () => siteSummaries.find((s) => siteKey(s.tenantId, s.siteId) === selectedKey) ?? null,
    [siteSummaries, selectedKey],
  );

  useEffect(() => {
    if (initialSiteKey) {
      setSelectedKey(initialSiteKey);
    }
  }, [initialSiteKey]);

  useEffect(() => {
    if (filteredSites.length === 0) {
      setSelectedKey(null);
      return;
    }
    if (!selectedKey || !filteredSites.some((s) => siteKey(s.tenantId, s.siteId) === selectedKey)) {
      setSelectedKey(siteKey(filteredSites[0]!.tenantId, filteredSites[0]!.siteId));
    }
  }, [filteredSites, selectedKey]);

  useEffect(() => {
    setAnchorPage(1);
  }, [anchorSearch, anchorFilters, anchorPageSize, selectedKey]);

  const anchorFilterOptions: FilterConfig[] = useMemo(
    () => [
      {
        field: 'assignmentState',
        label: 'Assignment',
        type: 'select',
        options: [
          { label: 'Assigned', value: 'assigned' },
          { label: 'Unassigned point', value: 'unassigned_point' },
          { label: 'Unassigned hardware', value: 'unassigned_hardware' },
        ],
      },
      {
        field: 'overallHealth',
        label: 'Health',
        type: 'select',
        options: [
          { label: 'Healthy', value: 'healthy' },
          { label: 'Degraded', value: 'degraded' },
          { label: 'Unhealthy', value: 'unhealthy' },
          { label: 'Unknown', value: 'unknown' },
          { label: 'N/A', value: 'not_applicable' },
        ],
      },
    ],
    [],
  );

  const siteAnchors = useMemo(() => {
    if (!selectedSite) return [];
    const key = siteKey(selectedSite.tenantId, selectedSite.siteId);
    return rows.filter((row) => rowSiteKey(row) === key);
  }, [rows, selectedSite]);

  const filteredAnchors = useMemo(() => {
    const q = anchorSearch.trim().toLowerCase();
    return siteAnchors.filter((row) => {
      if (anchorFilters.assignmentState && row.assignmentState !== anchorFilters.assignmentState) {
        return false;
      }
      if (anchorFilters.overallHealth && row.overallHealth !== anchorFilters.overallHealth) {
        return false;
      }
      if (!q) return true;
      const hay = [
        row.anchorSerialNumber ?? '',
        row.anchorPointLabel ?? '',
        row.floorName ?? '',
        ...(showDeviceMetadata
          ? [row.manufacturerName ?? '', row.deviceModel ?? '', row.firmwareVersion ?? '']
          : []),
      ].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [siteAnchors, anchorSearch, anchorFilters, showDeviceMetadata]);

  const pagedAnchors = useMemo(() => {
    const start = (anchorPage - 1) * anchorPageSize;
    return filteredAnchors.slice(start, start + anchorPageSize);
  }, [filteredAnchors, anchorPage, anchorPageSize]);

  const anchorColumns: TableColumn<SiteAnchorHealthRow>[] = useMemo(
    () => [
      {
        field: 'anchorSerialNumber',
        headerName: 'Satellite SN',
        width: 130,
        minWidth: 0,
        renderCell: (row) => <EllipsisCell text={row.anchorSerialNumber ?? '-'} />,
      },
      ...(showDeviceMetadata
        ? ([
            {
              field: 'manufacturerName',
              headerName: 'Manufacturer',
              width: 120,
              minWidth: 0,
              renderCell: (row: SiteAnchorHealthRow) => (
                <EllipsisCell text={row.manufacturerName ?? '-'} />
              ),
            },
            {
              field: 'deviceModel',
              headerName: 'Model',
              width: 110,
              minWidth: 0,
              renderCell: (row: SiteAnchorHealthRow) => (
                <EllipsisCell text={row.deviceModel ?? '-'} />
              ),
            },
            {
              field: 'firmwareVersion',
              headerName: 'Firmware',
              width: 110,
              minWidth: 0,
              renderCell: (row: SiteAnchorHealthRow) => (
                <EllipsisCell text={row.firmwareVersion ?? '-'} />
              ),
            },
          ] satisfies TableColumn<SiteAnchorHealthRow>[])
        : []),
      {
        field: 'anchorPointLabel',
        headerName: 'Point / Floor',
        width: 150,
        minWidth: 0,
        renderCell: (row) => {
          if (!row.anchorPointLabel && !row.assignmentState) return '-';
          if (row.assignmentState === 'unassigned_hardware') {
            return <EllipsisCell text="Unassigned hardware" />;
          }
          if (row.assignmentState === 'unassigned_point') {
            const text = row.anchorPointLabel ?? row.anchorPointId ?? 'Open point';
            return <EllipsisCell text={text} />;
          }
          const text = `${row.anchorPointLabel ?? '-'}${row.floorName ? ` (${row.floorName})` : ''}`;
          return <EllipsisCell text={text} />;
        },
      },
      {
        field: 'online',
        headerName: 'Online',
        width: 100,
        align: 'center',
        renderCell: (row) => {
          if (row.online == null) return '-';
          const lastSeenLabel = row.lastSeenAt ? dayjs(row.lastSeenAt).fromNow() : 'never';
          const tooltip = row.online
            ? `Location runtime reports online. Last UDP status: ${lastSeenLabel}.`
            : `Location runtime reports offline.${row.lastSeenAt ? ` Last UDP status: ${lastSeenLabel}.` : ''}`;
          return (
            <Tooltip title={tooltip}>
              <Chip
                size="small"
                label={row.online ? 'Online' : 'Offline'}
                color={row.online ? 'success' : 'error'}
              />
            </Tooltip>
          );
        },
      },
      {
        field: 'lastSeenAt',
        headerName: 'Last seen',
        width: 120,
        renderCell: (row) => (row.lastSeenAt ? dayjs(row.lastSeenAt).fromNow() : '-'),
      },
      {
        field: 'gatewayConfigSyncHealth',
        headerName: 'Config sync',
        width: 110,
        align: 'center',
        renderCell: (row) => row.gatewayConfigSyncHealth ?? '-',
      },
      {
        field: 'overallHealth',
        headerName: 'Health',
        width: 110,
        align: 'center',
        renderCell: (row) => {
          const tooltipParts = [row.healthReason].filter(Boolean);
          if (row.online === true && row.overallHealth === 'degraded' && row.lastSeenAt) {
            tooltipParts.push(
              'Health is degraded because the last UDP status is older than the fresh threshold (2 min), not because runtime marked the Satellite offline.',
            );
          }
          return (
            <Tooltip title={tooltipParts.join(' ')}>
              <Chip size="small" label={row.overallHealth} color={HEALTH_COLORS[row.overallHealth]} />
            </Tooltip>
          );
        },
      },
    ],
    [showDeviceMetadata],
  );

  const siteListPane = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        width: '100%',
      }}
    >
      <AppTextField
        size="small"
        placeholder="Search sites..."
        value={siteSearch}
        onChange={(e) => setSiteSearch(e.target.value)}
        sx={{ mb: 1.5, flexShrink: 0 }}
      />
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          pr: 0.5,
          ...scrollbarSx(theme),
        }}
      >
        {rowsLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        ) : filteredSites.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, px: 0.5 }}>
            No sites match your search.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {filteredSites.map((site) => {
              const key = siteKey(site.tenantId, site.siteId);
              return (
                <SiteAnchorHealthCard
                  key={key}
                  site={site}
                  selected={key === selectedKey}
                  tenantLabel={
                    showTenantOnCards ? (tenantNameById?.(site.tenantId) ?? site.tenantId) : null
                  }
                  onSelect={() => setSelectedKey(key)}
                />
              );
            })}
          </Stack>
        )}
      </Box>
    </Box>
  );

  const anchorTablePane = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        width: '100%',
      }}
    >
      {!selectedSite ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Select a site to view Satellite details.
          </Typography>
        </Box>
      ) : (
        <>
          <Stack
            direction="row"
            alignItems="baseline"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 1.5, flexShrink: 0, minWidth: 0, rowGap: 0.25 }}
          >
            <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ minWidth: 0 }}>
              {selectedSite.siteName ?? selectedSite.siteId}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0 }}>
              {selectedSite.totalAnchors} Satellite rows
              {selectedSite.assignedCount > 0 ? ` · ${selectedSite.assignedCount} assigned` : ''}
              {selectedSite.siteLocationGatewaySerial
                ? ` · gateway ${selectedSite.siteLocationGatewaySerial}`
                : ''}
            </Typography>
          </Stack>
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <EnhancedTable
              data={pagedAnchors}
              totalCount={filteredAnchors.length}
              columns={anchorColumns}
              getRowId={(row) =>
                `${row.tenantId}-${row.siteId}-${row.anchorPointId ?? row.anchorSerialNumber ?? 'row'}`
              }
              page={anchorPage}
              pageSize={anchorPageSize}
              onPageChange={setAnchorPage}
              onPageSizeChange={setAnchorPageSize}
              loading={rowsLoading}
              searchable
              searchTerm={anchorSearch}
              onSearchChange={setAnchorSearch}
              searchPlaceholder={
                showDeviceMetadata
                  ? 'Search serial, manufacturer, model, firmware...'
                  : 'Search Satellite serial, point...'
              }
              filters={anchorFilters}
              filterOptions={anchorFilterOptions}
              onFilterChange={(filters) => setAnchorFilters(filters as Record<string, string>)}
              maxHeight="100%"
              emptyStateTitle="No Satellites"
              emptyStateDescription={
                siteAnchors.length === 0
                  ? 'No Satellite data for this site.'
                  : 'No Satellites match your search or filters.'
              }
            />
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <TableDetailSplitLayout
      sx={{ flex: 1, minHeight: { xs: 'auto', lg: 480 }, minWidth: 0 }}
      tableWidth={SITE_LIST_DEFAULT_WIDTH}
      minTableWidth={SITE_LIST_MIN_WIDTH}
      maxTableWidth={SITE_LIST_MAX_WIDTH}
      resizable
      table={siteListPane}
      panel={anchorTablePane}
    />
  );
}
